import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import LotteryCodeField from '../../components/LotteryCodeField';
import DateField from '../../components/DateField';
import { fetchWinningTicketsReport } from '../../api/winningTicketsReport';
import { useAuth } from '../../context/AuthContext';
import { downloadCSV } from '../../utils/csv';
import { generateReportPdf } from '../../utils/pdf';
import { todayISO, daysAgoISO } from '../../utils/date';
import './WinningTicketsPage.css';

const searchByOptions = [
  { value: '', label: 'Tout' },
  { value: 'online_ontime', label: 'Online' },
  { value: 'local_ontime', label: 'Local (a temps)' },
  { value: 'local_overtime', label: 'Local (en retard)' },
  { value: 'all_local', label: 'Tout Local' },
];

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const WinningTicketsPage = () => {
  const { company } = useAuth();
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');

  const [dateFrom, setDateFrom] = useState(daysAgoISO(2));
  const [dateTo, setDateTo] = useState(todayISO());
  const [lottery, setLottery] = useState('0');
  const [lotteryName, setLotteryName] = useState('TOUS');
  const [paymentStatus, setPaymentStatus] = useState('All');
  const [searchBy, setSearchBy] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const filterApiValue = () => {
    if (filter === 'Groupe') return 'Group';
    if (filter === 'Superviseur') return 'Supervisor';
    if (filter === 'Banque') return 'Bank';
    return 'All';
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await fetchWinningTicketsReport({
        dateFrom, dateTo,
        filter: filterApiValue(),
        scope: filter === 'Tout' ? undefined : scopeId,
        tirage: lottery,
        paymentStatus,
        searchBy,
      });
      setData(result);
    } catch (err) {
      setError('Recherche impossible.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCsv = () => {
    if (!data || data.groups.length === 0) return;
    const rows = [['Tickets Gagnant'], [`Du ${data.date_from} au ${data.date_to}`], []];
    data.groups.forEach((g) => {
      rows.push([`${g.bank_code} - ${g.bank_name}`]);
      rows.push(['Loterie', 'SQ', 'No', 'Serial', 'Online/Local', 'Mise', 'Gain', 'Date', 'Heure', 'Statut']);
      g.rows.forEach((r) => rows.push([
        `${r.tirage_code}-${r.tirage_name}`, r.sq ?? '', r.no ?? '', r.serial ?? '',
        r.online ? 'Online' : (r.overtime ? 'Local (retard)' : 'Local'),
        r.total_amount, r.total_gain, r.date, r.time, r.estatus,
      ]));
      rows.push(['Total', '', '', '', '', g.totals.jugado, g.totals.gain, '', '', '']);
      rows.push([]);
    });
    rows.push(['Gran Total', '', '', '', '', data.grand_totals.jugado, data.grand_totals.gain]);
    downloadCSV('tickets-gagnant.csv', rows);
  };

  const handleDownloadPdf = async () => {
    if (!data || data.groups.length === 0) return;
    const sections = data.groups.map((g) => ({
      heading: `${g.bank_code} - ${g.bank_name}`,
      columns: ['Loterie', 'SQ/No', 'O/L', 'Mise', 'Gain', 'Date', 'Heure', 'Statut'],
      rows: g.rows.map((r) => [
        `${r.tirage_code}-${r.tirage_name}`,
        `${r.sq ?? '—'}/${r.no ?? '—'}`,
        r.online ? 'Online' : (r.overtime ? 'Local*' : 'Local'),
        money(r.total_amount), money(r.total_gain), r.date, r.time, r.estatus,
      ]),
    }));
    await generateReportPdf({
      company,
      title: `Tickets Gagnant (${data.date_from} au ${data.date_to})`,
      sections,
      filename: 'tickets-gagnant.pdf',
    });
  };

  return (
    <Layout>
      <p className="wt-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/rapports" className="bc-link">Reports</Link>
        {' >> '}
        <span className="bc-current">Tickets Gagnant</span>
      </p>

      <div className="wt-card">
        <div className="wt-card-header">
          <span className="wt-card-title">Winning Tickets Reports</span>
        </div>
        <div className="wt-card-body">
          <div className="wt-radio-row">
            {['Tout', 'Groupe', 'Superviseur', 'Banque'].map((opt) => (
              <label key={opt} className="wt-radio-label">
                <input
                  type="radio"
                  name="filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="wt-row wt-scope-row">
              <label className="wt-label">{filter}</label>
              {filter === 'Banque' ? (
                <BankScopeField
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, name) => { setScopeId(id); setScopeName(name); }}
                  inputClass="wt-input wt-input-sm"
                  iconClass="wt-icon-btn"
                  nameClass="wt-lottery-name"
                  errClass="wt-lookup-err"
                />
              ) : (
                <ScopeField
                  filter={filter}
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                  inputClass="wt-input wt-input-sm"
                  iconClass="wt-icon-btn"
                  nameClass="wt-lottery-name"
                  errClass="wt-lookup-err"
                />
              )}
            </div>
          )}

          <form onSubmit={handleSearch} className="wt-form">
            <div className="wt-row">
              <label className="wt-label">From</label>
              <DateField className="wt-input wt-input-date" value={dateFrom} onChange={setDateFrom} />

              <label className="wt-label wt-label-to">To</label>
              <DateField className="wt-input wt-input-date" value={dateTo} onChange={setDateTo} />
            </div>

            <div className="wt-row">
              <label className="wt-label">Loterie</label>
              <LotteryCodeField
                value={lottery}
                name={lotteryName}
                onChange={(code) => { setLottery(code); setLotteryName(''); }}
                onResolve={(code, nm) => { setLottery(code); setLotteryName(nm); }}
                inputClass="wt-input wt-input-sm"
                iconClass="wt-icon-btn"
                nameClass="wt-lottery-name"
              />
            </div>

            <div className="wt-row">
              <label className="wt-label">Payment Status</label>
              <select className="wt-select" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                <option value="All">All</option>
                <option value="Payed">Payed</option>
                <option value="Unpayed">Unpayed</option>
              </select>

              <label className="wt-label wt-label-to">Search By</label>
              <select className="wt-select" value={searchBy} onChange={(e) => setSearchBy(e.target.value)}>
                {searchByOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>

            {error && <p className="wt-error">{error}</p>}

            <button type="submit" className="wt-search-btn" disabled={loading}>
              {loading ? 'Recherche...' : 'Search'}
            </button>
          </form>
        </div>
      </div>

      {data && (
        <div className="wt-card wt-results">
          <div className="wt-card-header wt-results-header">
            <span className="wt-card-title">Reporte de Tickets Premiados</span>
            {data.groups.length > 0 && (
              <div className="wt-download-group">
                <button type="button" className="wt-download-btn" onClick={handleDownloadCsv}>Télécharger</button>
                <button type="button" className="wt-download-btn" onClick={handleDownloadPdf}>PDF</button>
              </div>
            )}
          </div>
          <div className="wt-card-body">
            <p className="wt-info-bar">Del: {data.date_from} Al: {data.date_to}</p>

            {data.groups.length === 0 ? (
              <p className="wt-empty">Aucun ticket gagnant sur cette portée/période.</p>
            ) : (
              <>
                {data.groups.map((g) => (
                  <div key={g.bank_id} className="wt-group">
                    <p className="wt-group-title">{g.bank_code} - {g.bank_name}</p>
                    <table className="wt-table">
                      <thead>
                        <tr>
                          <th>Lottery</th>
                          <th>O/L</th>
                          <th>SQ/No</th>
                          <th>Mise</th>
                          <th>Gagné</th>
                          <th>Date</th>
                          <th>Heure</th>
                          <th>Statut</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.rows.map((r) => (
                          <React.Fragment key={r.id}>
                            <tr>
                              <td>{r.tirage_code}-{r.tirage_name}</td>
                              <td>{r.online ? 'Online' : (r.overtime ? 'Local*' : 'Local')}</td>
                              <td>{r.sq ?? '—'}/{r.no ?? '—'}</td>
                              <td className="wt-money">{money(r.total_amount)}</td>
                              <td className="wt-money">{money(r.total_gain)}</td>
                              <td>{r.date}</td>
                              <td>{r.time}</td>
                              <td>{r.estatus}</td>
                              <td>
                                <span
                                  className="wt-toggle"
                                  onClick={() => setExpandedId((cur) => (cur === r.id ? null : r.id))}
                                >
                                  {expandedId === r.id ? '▲' : '▼'}
                                </span>
                              </td>
                            </tr>
                            {expandedId === r.id && (
                              <tr className="wt-detail-row">
                                <td colSpan={9}>
                                  <table className="wt-lines-table">
                                    <thead>
                                      <tr><th>Produit</th><th>Numero</th><th>Mise</th><th>Gain</th></tr>
                                    </thead>
                                    <tbody>
                                      {r.lines.map((ln, i) => (
                                        <tr key={i}>
                                          <td>{ln.product_abbr}</td>
                                          <td>{ln.numbers}</td>
                                          <td className="wt-money">{money(ln.amount)}</td>
                                          <td className="wt-money">{money(ln.gain)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                        <tr className="wt-total-row">
                          <td colSpan={2}><strong>Total : {g.totals.tickets} ticket(s)</strong></td>
                          <td></td>
                          <td className="wt-money"><strong>{money(g.totals.jugado)}</strong></td>
                          <td className="wt-money"><strong>{money(g.totals.gain)}</strong></td>
                          <td colSpan={4}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}

                <div className="wt-grand-total">
                  <span>Gran Total :</span>
                  <span>Tickets: {data.grand_totals.tickets}</span>
                  <span>{money(data.grand_totals.jugado)}</span>
                  <span>{money(data.grand_totals.gain)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default WinningTicketsPage;
